import React, { useMemo, useState, useEffect } from "react";
import { Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { postApiCall } from "../../../service/httpCall";
import { API_APIWEBHOOK_CREATE } from "../../../service/url";
import { ROUTES } from "../../../utilities/constant";
import ApiBuilder from "./components/ApiBuilder";
import CopyButton from "./components/CopyButton";
// import InstructionCard from "./components/InstructionCard";
import { toast } from "react-toastify";
import { fetchAndSetFlashMessage } from '../../../service/fetchFlashMessage';
import { FLASH_MESSAGE_ACTION_OBJECT, MODULE_ACL } from '../../../utilities/constant';

const CreateApiWebhook = () => {
  const navigate = useNavigate();

  const [webhook_name, setWebhookName] = useState("");
  const [request_type, setRequestType] = useState("");

  const [url, setUrl] = useState("");
  const [body_text, setBodyText] = useState("");
  const [payload, setPayload] = useState("");
  const [header, setHeader] = useState("");

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);

  useEffect(() => {
    fetchAndSetFlashMessage(
      msg => msg.module_name === MODULE_ACL.API_WEBHOOK && msg.action === FLASH_MESSAGE_ACTION_OBJECT.ADD && msg.type === 'Flash Message',
      setAlertMessage
    );
  }, []);

  const apiMethod = useMemo(() => {
    return request_type || "GET";
  }, [request_type]);

  const handleApiBuilderChange = (apiState) => {
    setUrl(apiState?.url || "");
    if (!apiState) {
      setHeader("");
      return;
    }

    if (apiState.headerMode === "raw") {
      setHeader((apiState.rawHeaders || "").trim());
      return;
    }

    const headerObj = (apiState.headers || []).reduce((acc, h) => {
      const key = (h?.key || "").trim();
      if (!key) return acc;
      acc[key] = h?.value ?? "";
      return acc;
    }, {});
    setHeader(Object.keys(headerObj).length ? JSON.stringify(headerObj) : "");
  };

  const handleUseUrl = (u) => setUrl(u || "");

  const handleBuilderResponse = (bodyString) => {
    setPayload(bodyString || "");
  };

  const validate = () => {
    const name = webhook_name.trim();
    const u = url.trim();
    const type = request_type;

    const urlRegex = /^(https?:\/\/)([\w\-._~:/?#[\]@!$&'()*+,;=]+)$/i;

    if (!name || !u || !type) {
      toast.error("Please fill all required fields.");
      return false;
    }
    if (!urlRegex.test(u)) {
      toast.error("Please enter a valid Webhook URL (must start with http:// or https://).");
      return false;
    }
    if (header && header.trim()) {
      try {
        JSON.parse(header);
      } catch {
        toast.error("Headers must be valid JSON (use Api Builder headers section).");
        return false;
      }
    }
    if (type === "GET" && !body_text.trim()) {
      toast.error("Body Text is required for GET requests.");
      return false;
    }
    if (["POST", "PUT", "PATCH", "DELETE"].includes(type) && !payload.trim()) {
      toast.error("Payload is required for this request type.");
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setShowConfirmModal(true);
  };

  const submitWebhook = async () => {
    setShowConfirmModal(false);
    setSaving(true);
    const body = {
      webhook_name: webhook_name.trim(),
      url: url.trim(),
      request_type: request_type,
      body_text: request_type === "GET" ? body_text : null,
      payload: ["POST", "PUT", "PATCH", "DELETE"].includes(request_type) ? payload : null,
      header: header && header.trim() ? header.trim() : null,
      status: "1",
    };

    try {
      await postApiCall(API_APIWEBHOOK_CREATE, body);
      toast.success("API Webhook created successfully!");
      navigate(`/${ROUTES.APIWEBHOOK.LIST}`);
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {alertMessage && (
        <div className="alert alert-success marquee-container" style={{ marginTop: "5px" }}>
          <span className="marquee-text" style={{ '--marquee-duration': `${(alertMessage.length * 0.2) + 15}s` }}>
            {alertMessage}
          </span>
        </div>
      )}
      <div className="page-wrapper">
        <Card className="page-card">
          <Card.Header className="page-header">Add API Webhook</Card.Header>
          <Card.Body>
            <Row className="g-4">
              <Col xs={12} md={12} className="left-panel">
                <Card className="section-card">
                  <Card.Body>
                    <Form onSubmit={handleSubmit}>
                      <Row>
                        <Col sm={6}>
                          <Form.Group className="form-group required">
                            <Form.Label>
                              Webhook Name:<span style={{ color: "red", margin: "0 4px" }}>*</span>
                            </Form.Label>
                            <Form.Control
                              required
                              name="webhook_name"
                              type="text"
                              placeholder="Webhook Name"
                              value={webhook_name}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (/^[A-Za-z]*$/.test(value)) {
                                  setWebhookName(value);
                                }
                              }}
                              autoComplete="off"
                              pattern="^[A-Za-z]+$"
                              title="Only letters allowed. No numbers, spaces, or special characters."
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={6}>
                          <Form.Group className="form-group required">
                            <Form.Label>
                              Request Type:<span style={{ color: "red", margin: "0 4px" }}>*</span>
                            </Form.Label>
                            <Form.Select
                              required
                              name="request_type"
                              value={request_type}
                              onChange={(e) => setRequestType(e.target.value)}
                            >
                              <option value="">Select Request Type</option>
                              <option value="GET">GET</option>
                              <option value="POST">POST</option>
                              <option value="PUT">PUT</option>
                              <option value="PATCH">PATCH</option>
                              <option value="DELETE">DELETE</option>
                              <option value="HEAD">HEAD</option>
                              <option value="OPTIONS">OPTIONS</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>

                      {request_type && (
                        <Row className="mb-2">
                          <Col>
                            <ApiBuilder
                              initialUrl={url}
                              initialMethod={apiMethod}
                              onUseUrl={handleUseUrl}
                              onMethod={(m) => {
                                const next =
                                  String(m || "").toUpperCase() === "GET" ? "GET" : "POST";
                                setRequestType(next);
                              }}
                              onResponse={handleBuilderResponse}
                              onStateChange={handleApiBuilderChange}
                            />
                          </Col>
                        </Row>
                      )}

                      <Row>
                        {request_type === "GET" && (
                          <Col className="position-relative">
                            <Form.Group className="form-group">
                              <Form.Label>
                                Note: Please paste the body text here{" "}
                                <span style={{ color: "red", margin: "0 4px" }}>*</span>
                              </Form.Label>
                              <div className="d-flex justify-content-end mb-2">
                                <CopyButton text={body_text} />
                              </div>
                              <Form.Control
                                required
                                name="body_text"
                                value={body_text}
                                as="textarea"
                                rows={6}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\s+$/.test(value)) return;
                                  setBodyText(value);
                                }}
                                onBlur={(e) => {
                                  const trimmed = e.target.value.trim();
                                  setBodyText(trimmed);
                                }}
                              />
                            </Form.Group>
                          </Col>
                        )}

                        {["POST", "PUT", "PATCH", "DELETE"].includes(request_type) && (
                          <Col className="position-relative">
                            <Form.Group className="form-group">
                              <Form.Label>
                                Note: Payload / Request Body
                              </Form.Label>
                              <div className="d-flex justify-content-end mb-2">
                                <CopyButton text={payload} />
                              </div>
                              <Form.Control
                                required
                                name="payload"
                                value={payload}
                                onChange={(e) => setPayload(e.target.value)}
                                as="textarea"
                                rows={6}
                              />
                            </Form.Group>
                          </Col>
                        )}
                      </Row>


                      <Button
                        variant="primary"
                        className="mt-3"
                        type="submit"
                        disabled={saving}
                      >
                        Add Api Webhook
                      </Button>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>

              {/* <Col xs={12} md={5} className="right-panel">
                <InstructionCard />
              </Col> */}
            </Row>
          </Card.Body>
        </Card>
      </div>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure the {["POST", "PUT", "PATCH", "DELETE"].includes(request_type) ? "payload" : "body text"} is
          correct?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            No
          </Button>
          <Button variant="primary" onClick={submitWebhook}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateApiWebhook;
